// Port of ios/Sources/ViewModels/InlineMarkdown.swift.
//
// The inline Markdown Mattermost messages actually contain: links, bold, italic, inline code
// and strikethrough. Until now the app rendered `post.message` raw, so a posted link showed
// literally as `[label](url)` — the same screenshot bug Android fixed. A decision-for-decision
// port of Android's `InlineMarkdown`, tests included.
//
// Deliberately NOT a Markdown library: this is the inline subset, it must never throw, and the
// output is plain data (text + spans) rather than anything view-shaped. `MessageBody` turns
// `ParsedText` into styled text. Block-level Markdown is out of scope; anything
// unrecognised, unterminated or malformed falls through as literal text — the only acceptable
// failure mode: a message must always be readable.
//
// WEB: the scanner walks Swift Characters (grapheme clusters, see swiftCharacters.ts) so every
// decision matches the Apple client, but span offsets are UTF-16 code units into
// `ParsedText.text` (Swift: Character counts) — what `text.slice(start, end)` wants.

import { characterView, isLetter, isNumber, isWhitespace, trimWhitespaces } from './swiftCharacters';

export type InlineStyle = 'bold' | 'italic' | 'code' | 'strikethrough' | 'link' | 'image';

/** Offsets are UTF-16 code units into `ParsedText.text` (Swift: Character counts). */
export interface InlineSpan {
  start: number;
  end: number;
  style: InlineStyle;
  url?: string;
  /** An `image` span's alt text — the only thing left to say if the image never loads. */
  alt?: string;
}

export interface ParsedText {
  text: string;
  spans: InlineSpan[];
  /** Swift's computed `isPlain` (`spans.isEmpty`), stored. */
  isPlain: boolean;
}

/**
 * What an `image` span occupies in `ParsedText.text`: one OBJECT REPLACEMENT CHARACTER,
 * the Unicode codepoint that exists to mean "something not-text sits here".
 *
 * It has to be exactly one character and it has to be IN the text. The renderer swaps it for
 * the picture, and every other pass over the parsed text — mentions, channel links, emoji —
 * addresses offsets in that same string. Emitting nothing would slide all of them; emitting
 * the alt text would put a word on screen where a picture belongs. (U+FFFC is one UTF-16 unit,
 * so the span is 1 wide in the web's coordinates too.)
 *
 * Anything that flattens a message to one line (`ChannelListDerivation.messagePreview`)
 * strips it, because a row cannot draw the picture and a lone ￼ box is worse than a gap.
 */
const imagePlaceholder = '￼';

const maxInput = 20_000;
const maxDepth = 6;

function parse(input: string): ParsedText {
  // A pathological message should degrade to plain text rather than spend a frame budget
  // in the parser. (Swift counts Characters; more than 20 000 UTF-16 units may still be
  // ≤ 20 000 Characters, so only then is the exact count taken.)
  if (input.length > maxInput && characterView(input).chars.length > maxInput) {
    return { text: input, spans: [], isPlain: true };
  }
  const out = new Output();
  scan(characterView(input).chars as string[], out, 0);
  const text = out.text();
  return { text, spans: out.spans, isPlain: out.spans.length === 0 };
}

/** The `[Character]` buffer plus its running UTF-16 length, so spans can be measured in units. */
class Output {
  private parts: string[] = [];
  length = 0; // UTF-16 units
  spans: InlineSpan[] = [];
  append(c: string): void {
    this.parts.push(c);
    this.length += c.length;
  }
  appendAll(cs: readonly string[]): void {
    for (const c of cs) this.append(c);
  }
  text(): string {
    return this.parts.join('');
  }
}

// MARK: - The scanner (all on Characters so decisions are grapheme decisions)

function scan(input: string[], out: Output, depth: number): void {
  let i = 0;
  while (i < input.length) {
    const c = input[i];

    // Backslash escape: the NEXT character is always literal, never a delimiter.
    if (c === '\\' && i + 1 < input.length && isDelimiter(input[i + 1])) {
      out.append(input[i + 1]);
      i += 2;
      continue;
    }

    if (c === '`') {
      const close = find('`', input, i + 1);
      if (close !== null && close > i + 1) {
        const start = out.length;
        // Code is verbatim — no recursion, no escapes.
        out.appendAll(input.slice(i + 1, close));
        out.spans.push({ start, end: out.length, style: 'code' });
        i = close + 1;
        continue;
      }
    }

    // `![alt](url)` — an IMAGE, and it must be tested before the link case below, because
    // the link case matches the `[alt](url)` inside it and leaves the `!` stranded. That is
    // exactly what shipped: our WhatsApp bridge prefixes every relayed message with the
    // sender's avatar (`![Image](…jpg)  **Name:**`, 14k posts since 2024-11-21), and every
    // one of them rendered as a blue, tappable, meaningless "!Image" (user screenshot,
    // 2026-09-01). Falls through to the link case if the `[…](…)` half does not parse, so a
    // stray "!" before an ordinary bracket still behaves as it always did.
    if (c === '!' && i + 1 < input.length && input[i + 1] === '[') {
      const image = parseLink(input, i + 1);
      if (image) {
        const start = out.length;
        out.append(imagePlaceholder);
        const span: InlineSpan = { start, end: out.length, style: 'image', url: image.url };
        if (image.rawLabel !== '') span.alt = image.rawLabel;
        out.spans.push(span);
        i = image.nextIndex;
        continue;
      }
    }

    if (c === '[') {
      const link = parseLink(input, i);
      if (link) {
        const start = out.length;
        if (depth < maxDepth) {
          scan(characterView(link.label).chars as string[], out, depth + 1);
        } else {
          out.append(link.label);
        }
        out.spans.push({ start, end: out.length, style: 'link', url: link.url });
        i = link.nextIndex;
        continue;
      }
    }

    if (startsWith(input, i, '**') || startsWith(input, i, '__')) {
      const marker = input[i] + input[i + 1];
      const close = find(marker, input, i + 2);
      if (close !== null && close > i + 2) {
        appendStyled(input.slice(i + 2, close), 'bold', out, depth);
        i = close + 2;
        continue;
      }
    }

    if (startsWith(input, i, '~~')) {
      const close = find('~~', input, i + 2);
      if (close !== null && close > i + 2) {
        appendStyled(input.slice(i + 2, close), 'strikethrough', out, depth);
        i = close + 2;
        continue;
      }
    }

    if ((c === '*' || c === '_') && isEmphasisOpener(input, i)) {
      const close = findEmphasisCloser(input, i);
      if (close !== null) {
        appendStyled(input.slice(i + 1, close), 'italic', out, depth);
        i = close + 1;
        continue;
      }
    }

    const autolink = matchAutolink(input, i);
    if (autolink) {
      const start = out.length;
      out.appendAll(autolink);
      out.spans.push({ start, end: out.length, style: 'link', url: autolink.join('') });
      i += autolink.length;
      continue;
    }

    out.append(c);
    i += 1;
  }
}

function appendStyled(inner: string[], style: InlineStyle, out: Output, depth: number): void {
  const start = out.length;
  if (depth < maxDepth) {
    scan(inner, out, depth + 1);
  } else {
    out.appendAll(inner);
  }
  // A delimiter pair around nothing ("****") produces no span rather than a zero-length
  // one, which some text renderers treat as covering the rest of the string.
  if (out.length > start) {
    out.spans.push({ start, end: out.length, style });
  }
}

/**
 * `label` is what a LINK draws — the URL itself when the source wrote no label, so the link
 * is never invisible. `rawLabel` is what was actually written between the brackets, which is
 * what an IMAGE needs: its label is alt text, and an image with no alt is completely ordinary.
 * Using `label` there would caption every unlabelled image with its own URL.
 */
interface Link {
  label: string;
  rawLabel: string;
  url: string;
  nextIndex: number;
}

/**
 * `[label](url)`. Bracket and paren nesting are both tracked, so a label containing `]` or
 * a URL containing `(` — both real, e.g. Wikipedia links — do not truncate the match.
 */
function parseLink(input: string[], open: number): Link | null {
  let depth = 0;
  let closeBracket = -1;
  for (let i = open; i < input.length; i++) {
    if (input[i] === '[') depth += 1;
    if (input[i] === ']') {
      depth -= 1;
      if (depth === 0) {
        closeBracket = i;
        break;
      }
    }
  }
  if (closeBracket === -1 || !(closeBracket + 1 < input.length) || input[closeBracket + 1] !== '(') return null;

  let parens = 0;
  let closeParen = -1;
  for (let i = closeBracket + 1; i < input.length; i++) {
    if (input[i] === '(') parens += 1;
    if (input[i] === ')') {
      parens -= 1;
      if (parens === 0) {
        closeParen = i;
        break;
      }
    }
  }
  if (closeParen === -1) return null;

  const label = input.slice(open + 1, closeBracket).join('');
  const url = trimWhitespaces(input.slice(closeBracket + 2, closeParen).join(''));
  if (url === '') return null;
  // An empty label would render as an invisible, untappable link.
  return { label: label === '' ? url : label, rawLabel: label, url, nextIndex: closeParen + 1 };
}

/**
 * A single `*`/`_` only opens emphasis at a word boundary and when not followed by a
 * space. Without the boundary rule every `snake_case` identifier and every `2 * 3` in a
 * message turns into italics, which is worse than not supporting italics at all.
 */
function isEmphasisOpener(input: string[], i: number): boolean {
  if (!(i + 1 < input.length)) return false;
  const after = input[i + 1];
  if (isWhitespace(after)) return false;
  if (i > 0) {
    const before = input[i - 1];
    if (isLetter(before) || isNumber(before) || before === '_') return false;
  }
  return true;
}

function findEmphasisCloser(input: string[], open: number): number | null {
  const marker = input[open];
  for (let i = open + 1; i < input.length; i++) {
    if (input[i] !== marker) continue;
    if (isWhitespace(input[i - 1])) continue;
    if (i + 1 < input.length) {
      const after = input[i + 1];
      if (isLetter(after) || isNumber(after) || after === '_') continue;
    }
    return i;
  }
  return null;
}

const trailingPunctuation = new Set('.,;:!?)"\''.split(''));

/** Bare URLs, which people paste far more often than they write Markdown links. */
function matchAutolink(input: string[], i: number): string[] | null {
  if (!(startsWith(input, i, 'http://') || startsWith(input, i, 'https://'))) return null;
  let end = i;
  while (end < input.length && !isWhitespace(input[end]) && input[end] !== '<' && input[end] !== '>') end += 1;
  // Trailing sentence punctuation belongs to the sentence, not to the URL.
  while (end > i && trailingPunctuation.has(input[end - 1])) end -= 1;
  const url = input.slice(i, end);
  return url.length > 'https://'.length ? url : null;
}

const delimiters = new Set('*_`~[]()\\'.split(''));

function isDelimiter(c: string): boolean {
  return delimiters.has(c);
}

/** `prefix` is ASCII, one Character per code unit. */
function startsWith(input: string[], index: number, prefix: string): boolean {
  if (index + prefix.length > input.length) return false;
  for (let offset = 0; offset < prefix.length; offset++) {
    if (input[index + offset] !== prefix[offset]) return false;
  }
  return true;
}

function find(needle: string, input: string[], start: number): number | null {
  if (needle.length === 0) return null;
  let i = start;
  while (i + needle.length <= input.length) {
    if (startsWith(input, i, needle)) return i;
    i += 1;
  }
  return null;
}

export const InlineMarkdown = {
  imagePlaceholder,
  parse,
} as const;
